import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'

import { types, initialState } from 'store/MemberCommunication'

/**
 * Fetches an array of thumbnail attachments for each comment
 *
 * @param {string} memberUuid Required member uuid
 * @param {string} ticketId Required ticket id
 * @param {string} commentId Required comment id
 */
export const fetchAttachmentThumbnail = (memberUuid, ticketKey, commentId) => {
  const payloadRequest = {
    ticketId: ticketKey,
    commentId,
    memberUuid,
  }
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'comm',
        `jira/member/attachment/thumbnail${makeQueryParams(payloadRequest)}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_ATTACHMENT_THUMBNAIL_REQUEST,
        {
          type: types.FETCH_ATTACHMENT_THUMBNAIL_SUCCESS,
          meta: { commentId },
        },
        types.FETCH_ATTACHMENT_THUMBNAIL_ERROR,
      ],
    },
  }
}

export const clearAttachmentThumbnails = () => ({
  type: types.CLEAR_ATTACHMENT_THUMBNAILS,
})

// Download attachments to the local machine
export const downloadAttachment = (
  attachmentId,
  ticketId,
  memberUuid,
  filename,
) => {
  const endpoint = getApiPath(
    'comm',
    `jira/member/attachment/${attachmentId}/download${makeQueryParams({
      memberUuid,
      ticketId,
    })}`,
  )

  return {
    [RSAA]: {
      endpoint,
      method: 'GET',
      types: [
        types.DOWNLOAD_ATTACHMENT_REQUEST,
        {
          type: types.DOWNLOAD_ATTACHMENT_SUCCESS,
          // The id is needed to name the file when Jira has no filename for it
          meta: { filename, attachmentId },
          payload: async (action, state, res) => ({
            blob: await res.blob(),
            // The best name for the file, when the server sends one
            contentDisposition: res.headers.get('content-disposition'),
          }),
        },
        types.DOWNLOAD_ATTACHMENT_ERROR,
      ],
    },
  }
}

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    // Every message in a conversation fetches its own thumbnails, so these
    // cases have to keep the entities already in the store. Dropping them
    // wiped out attachments the member could previously see.
    case types.FETCH_ATTACHMENT_THUMBNAIL_REQUEST:
      return {
        ...state,
        attachmentThumbnails: {
          ...state.attachmentThumbnails,
          busy: true,
          error: null,
        },
      }

    case types.FETCH_ATTACHMENT_THUMBNAIL_SUCCESS:
      return {
        ...state,
        attachmentThumbnails: {
          ...state.attachmentThumbnails,
          busy: false,
          error: null,
          entities: {
            ...state.attachmentThumbnails.entities,
            [meta.commentId]: payload,
          },
        },
      }

    case types.FETCH_ATTACHMENT_THUMBNAIL_ERROR:
      return {
        ...state,
        attachmentThumbnails: {
          ...state.attachmentThumbnails,
          busy: false,
          error: payload,
        },
      }

    case types.CLEAR_ATTACHMENT_THUMBNAILS:
      return {
        ...state,
        attachmentThumbnails: {
          busy: false,
          error: null,
          entities: {},
        },
      }

    case types.DOWNLOAD_ATTACHMENT_REQUEST:
      return {
        ...state,
        attachment: {
          busy: true,
          error: null,
        },
      }

    case types.DOWNLOAD_ATTACHMENT_SUCCESS:
      // Leverage middleware to handle the download of the attachment since the
      // download endpoint response doesn't need to be stored in the state
      // see middleware for more info: apps/member/src/middleware/downloadAttachment.js
      return {
        ...state,
        attachment: {
          busy: false,
          error: null,
        },
      }

    case types.DOWNLOAD_ATTACHMENT_ERROR:
      return {
        ...state,
        attachment: {
          busy: false,
          error: payload,
        },
      }

    default:
      return state
  }
}
